import {EventForm} from '../../forms/EventForm';
import {InsertEventDto} from '../../../generated/models/InsertEventDto';
import {UpdateBulkEventDto} from '../../../generated/models/UpdateBulkEventDto';
import {apiRequest} from '../../../common/services/api';
import {
  getOrganizerEventDetailsEndpoint,
  updateEventEndpoint,
  updateBulkEventEndpoint,
} from '../../../common/constants/endpoints';
import {useOrganizerStore} from '../../../common/stores/useOrganizerStore';
import {useEffect, useState} from 'react';
import {useUiStore} from '../../../common/stores/useUiStore';
import {useLocation, useNavigate} from 'react-router-dom';
import routes from '../../routes/routes';
import {useEventDtoStore, useEventListStore} from '../../../common';
import {useTagsStore} from '../../../common/stores/useTagsStore';
import {EventDetailDto} from '../../../generated/models/EventDetailDto';
import {EventOccurrenceDto} from '../../../generated/models/EventOccurrenceDto';
import {parseApiError} from '../../../utils/apiError';
import {mapEventDetailDto} from '../../../utils/mappers';
import {MessageCard} from '../../card/messageCard';

export default function EditEventScreen() {
  const {organizer} = useOrganizerStore();
  const navigate = useNavigate();
  const {
    setIsLoading,
    clearOnBackButtonClick,
    setOnBackButtonClick,
    setShowBackButton,
  } = useUiStore(state => state);
  const {clearSelectedTagCodes, selectedTagCodes, setSelectedTagCodes} =
    useTagsStore();
  const {clearEventDto, eventDto, setEventDto} = useEventDtoStore();
  const selectedEventId = useEventListStore(state => state.selectedEventId);
  const location = useLocation();
  const [apiError, setApiError] = useState<string | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    setShowBackButton(true);
    setOnBackButtonClick(() => {
      if (location.state?.returnTo) {
        navigate(location.state.returnTo);
        return;
      }
      navigate(routes.getAllEvents, {
        state: {
          searchText: location.state?.searchText,
          scrollPosition: location.state?.initialScrollPosition,
        },
      });
    });
    return () => {
      setShowBackButton(false);
      clearOnBackButtonClick();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const getEventDetails = async () => {
      setIsLoading(true);
      const response = await apiRequest<EventDetailDto>({
        endpoint: getOrganizerEventDetailsEndpoint(
          organizer?.id || '',
          selectedEventId || '',
        ),
        method: 'GET',
      });

      setIsLoading(false);

      if (!response?.data) {
        alert('Something went wrong when retrieving event data');
        return;
      }

      const mapped = mapEventDetailDto(response.data);
      setEventDto(mapped);
      setSelectedTagCodes(mapped.tagCodes);
    };

    getEventDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleUpdateEvent = async (data: InsertEventDto[]) => {
    setApiError(null);
    const item = data[0];
    const isRecurring = data.length > 1 || (eventDto?.groupId && (eventDto?.occurrences?.length ?? 0) > 1);

    if (isRecurring) {
      const bulkBody: UpdateBulkEventDto = {
        name: item.name,
        description: item.description,
        locationCode: item.locationCode,
        coordinates: item.coordinates,
        address: item.address,
        tagCodes: selectedTagCodes ?? [],
        mapImageCdnPaths: item.mapImageCdnPaths,
        occurrences: data.map((d, i) => {
          const existingId = (eventDto?.occurrences as EventOccurrenceDto[])?.[i]?.eventId || '';
          return {
            eventId: existingId,
            startDateTime: new Date(d.startDateTime).toISOString(),
            endDateTime: new Date(d.endDateTime).toISOString(),
          };
        }),
      };

      const response = await apiRequest<string, UpdateBulkEventDto>(
        {
          endpoint: updateBulkEventEndpoint(organizer?.id || ''),
          method: 'PUT',
          body: bulkBody as any,
        },
        {onError: err => setApiError(parseApiError(err) ?? 'Could not edit bulk event. Something went wrong')},
      );

      setIsLoading(false);

      if (!response?.data) {
        return;
      }
    } else {
      const response = await apiRequest<string, InsertEventDto>(
        {
          endpoint: updateEventEndpoint(organizer?.id || '', eventDto?.id || ''),
          method: 'PUT',
          body: {
            ...item,
            startDateTime: item.startDateTime.toISOString(),
            endDateTime: item.endDateTime.toISOString(),
            tagCodes: selectedTagCodes,
          } as any,
        },
        {onError: err => setApiError(parseApiError(err) ?? 'Could not edit event. Something went wrong')},
      );

      setIsLoading(false);

      if (!response?.data) {
        return;
      }
    }
    clearEventDto();
    clearSelectedTagCodes();
    if (location.state?.returnTo) {
      navigate(location.state.returnTo);
      return;
    }
    navigate(routes.getAllEvents, {
      state: {
        searchText: location.state?.searchText,
        // scrollPosition: location.state?.initialScrollPosition,
      },
    });
  };

  return (
    <div
      style={{
        maxWidth: 500,
        margin: '40px auto', // Centered and spaced from header
        padding: '40px',
        backgroundColor: '#353535', // Dashboard background
        borderRadius: '16px',
        boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
      }}>
      <h1 style={{color: '#fff', marginBottom: '30px', textAlign: 'center'}}>
        Edit Event
      </h1>
      <EventForm
        type="EDIT"
        onSubmit={handleUpdateEvent}
        eventData={eventDto}
      />
      <MessageCard type={apiError ? 'error' : null} message={apiError} />
    </div>
  );
}
